import React, { Component, useState } from 'react';
import { array, bool, func, oneOf, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  getQueryParamNames,
  isOriginInUse,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { FieldBoolean, H3, H5, Icons, LayoutSingleColumn, NamedLink, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import Slider from 'react-slick';
import '../../styles/slick/slick.css';
import '../../styles/slick/slick-theme.css';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import { withViewport } from '../../util/uiHelpers';
import SearchBar from './SearchBar';
import FilterModal from './FilterModal/FilterModal';
import FilterCategories from './FilterSidebar/FilterCategories';
import SearchPagePills from './SearchPagePills/SearchPagePills';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

const links = [
  { name: 'LandingPage', text: 'landingpage' },
  { name: 'LandingPage', text: 'landingpage' },
  { name: 'LandingPage', text: 'landing' },
  { name: 'SearchPage', text: 'webdesign' },
  { name: 'SearchPage', text: 'website' },
  { name: 'LandingPage', text: 'landingpage' },
  { name: 'LandingPage', text: 'homepage' },
];

const categories = [
  { id: 'SearchPage.talent', search: 'pub_listingCategory=talent' }, // General Talent Category
  { id: 'SearchPage.brand', search: 'pub_listingCategory=brand' },
  { id: 'SearchPage.group', search: 'pub_listingCategory=school' }, // Covers Schools, Collectives, Teams
  // { id: 'SearchPage.agency', search: 'pub_listingCategory=agency' },
];

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isFilterModalOpen: false,
      isClassAdded: false,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.onOpenFilterModal = this.onOpenFilterModal.bind(this);

    // filter toggle
    this.handleChildClick = this.handleChildClick.bind(this);
  }
  handleChildClick() {
    this.setState(prevState => ({ isClassAdded: !prevState.isClassAdded }));
  }
  componentDidMount() {
    this.setState({ isClassAdded: false }); // Ensures it's collapsed on first load
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }
  onOpenFilterModal() {
    this.setState({ isFilterModalOpen: !this.state.isFilterModalOpen });
  }
  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      viewport,
      sliderSettings,
      history,
    } = this.props;
    const isMobileLayout =
      viewport.width && viewport.width > 0 && viewport.width < MODAL_BREAKPOINT;
    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    const handleSearch = (searchInput, category) => {
      const searchParams = {
        ...(searchInput ? { keywords: searchInput.toLowerCase() } : {}),
        pub_listingCategory: category.toLowerCase(),
      };

      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
    };
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <LayoutSingleColumn
          className={css.pageRoot}
          topbar={<TopbarContainer viewport={viewport} currentSearchParams={validQueryParams} />}
          footer={<FooterContainer />}
        >
          <div className={css.layoutWrapperContainer}>
            {!isMobileLayout ? (
              <div className={css.contentWidth}>
                {/* <div className={css.searchBarContainer}>
                <div className={css.searchKeywordSec}>
                  <Icons name="search" />
                  <input type="text" placeholder="Search..." />
                </div>
                <div className={css.chooseCategorySec}>
                  <select name="" id="">
                    <option value="talent">Talent</option>
                    <option value="brand">Brand</option>
                    <option value="agency">Agency</option>
                    <option value="school">School</option>
                  </select>
                </div>
              </div> */}
                <div className={css.relatedContent}>
                  <h2>
                    <FormattedMessage id="SearchPage.heading" />
                  </h2>
                  <p>
                    <FormattedMessage id="SearchPage.description" />
                  </p>

                  {/* <div className={css.relatedTopic}>
                    <strong>
                      <FormattedMessage id="SearchPage.related" />
                    </strong>
                    {links.map((link, index) => (
                      <NamedLink key={index} name={link.name} className={css.link}>
                        {link.text}
                      </NamedLink>
                    ))}
                  </div> */}
                </div>
              </div>
            ) : null}
            <div className={css.layoutWrapperMain} role="main">
              <div className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
                <div className={css.categoryFilterSec}>
                  <div className={css.sortingFilter}>
                    {/* {sortConfig.active ? sortBy('desktop') : null} */}
                    {/* <MainPanelHeader
                    className={css.mainPanel}
                    sortByComponent={sortBy('desktop')}
                    isSortByActive={sortConfig.active}
                    listingsAreLoaded={listingsAreLoaded}
                    resultsCount={totalItems}
                    searchInProgress={searchInProgress}
                    searchListingsError={searchListingsError}
                    noResultsInfo={noResultsInfo}
                  /> */}
                  </div>
                  <div className={css.topCategories}>
                    {categories.map((category, index) => {
                      const isActive = location?.search?.includes(category?.search); // Check if the URL contains the category search query
                      return (
                        <NamedLink
                          key={index}
                          name="SearchPage"
                          to={{ search: category.search }}
                          className={classNames(css.category, { [css.active]: isActive })}
                        >
                          <FormattedMessage id={category.id} />
                        </NamedLink>
                      );
                    })}
                    {/* {intl.formatMessage({
                        id: 'SearchPage.or',
                      })} */}
                  </div>

                  {/* <div className={css.toggleFilterSec}>
                      <button type="button" onClick={this.onOpenFilterModal}>
                        <Icons name="filterLines" /> <FormattedMessage id="SearchPage.filters" />
                      </button>
                    </div> */}
                </div>

                {/* <div className={css.filterColumnContent}>
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersDesktop"
                        className={css.filter}
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={true}
                        isDesktop
                      />
                    );
                  })}
                  {/* <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                    <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                  </button>
                </div> */}
              </div>

              {/* <SearchFiltersMobile
                  className={css.searchFiltersMobileList}
                  urlQueryParams={validQueryParams}
                  sortByComponent={sortBy('mobile')}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  showAsModalMaxWidth={MODAL_BREAKPOINT}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onOpenModal={this.onOpenMobileModal}
                  onCloseModal={this.onCloseMobileModal}
                  resetAll={this.resetAll}
                  selectedFiltersCount={selectedFiltersCountForMobile}
                  isMapVariant={false}
                  noResultsInfo={noResultsInfo}
                >
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;

                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersMobile"
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersMobile> */}

              <div
                className={classNames(
                  css.searchResultContent,
                  this.state.isClassAdded ? css.expanded : null
                )}
              >
                {!this.state.isClassAdded ? (
                  <div className={css.filterSec}>
                    <FilterCategories
                      onHandleChildClick={this.handleChildClick}
                      config={config}
                      onHandleSearchChange={handleSearch}
                      viewport={viewport}
                    />
                  </div>
                ) : null}
                <div className={css.searchListingsSec}>
                  <div className={css.filterAppliedSec}>
                    <div className={css.filterAppliedStart}>
                      {this.state.isClassAdded ? (
                        <span className={css.collapseFilter} onClick={this.handleChildClick}>
                          <Icons name="filter" /> Filter
                        </span>
                      ) : null}
                      <SearchPagePills
                        history={history}
                        routeConfiguration={routeConfiguration}
                        location={location}
                        intl={intl}
                      />
                    </div>
                    <SearchBar onSearch={handleSearch} />
                  </div>
                  {searchListingsError ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.searchError" />
                    </H3>
                  ) : null}
                  {!isValidDatesFilter ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.invalidDatesFilter" />
                    </H3>
                  ) : null}
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <FilterModal
            isOpen={this.state.isFilterModalOpen}
            onClose={() => this.setState({ isFilterModalOpen: false })}
            onManageDisableScrolling={onManageDisableScrolling}
            getHandleChangedValueFn={this.getHandleChangedValueFn}
            totalItems={totalItems}
            initialValues={initialValues(this.props, this.state.currentQueryParams)}
            availableFilters={availableFilters}
            urlQueryParams={validQueryParams}
            resetAll={this.resetAll}
            marketplaceCurrency={marketplaceCurrency}
            listingCategories={listingCategories}
          />
        </LayoutSingleColumn>
      </Page>
    );
  }
}

const sliderSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  autoplay: false,
  autoplaySpeed: 2200,
  speed: 500,
  cssEase: 'ease',
  slidesToShow: 1,
  slidesToScroll: 1,
  variableWidth: true,
  focusOnSelect: false,
  centerMode: false,
  responsive: [
    {
      breakpoint: 1128,
      settings: {
        slidesToShow: 1,
      },
    },
    {
      breakpoint: 991,
      settings: {
        slidesToShow: 1,
      },
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
      },
    },
  ],
};

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  sliderSettings,
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};
const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport
)(EnhancedSearchPage);

export default SearchPage;
